import { StyleSheet, View } from "react-native";
import React from "react";
import RunnerSilkAndNumbers from "./RunnerSilkAndNumbers";
import variables from "../../../../../styles/variables";
import RunnerDetails from "../../../../Racecard/RunnerDetails";
import Gap from "../../../../Gap";
import RText from "../../../../RText";
import StandardPriceButton from "../../../../Buttons/StandardPriceButton";
import TimeformStarRating from "./TimeformStarRating";
import { useResponsive } from "../../../../../hooks/useResponsive";
import ExpandedRacecardRunnerRow from "./ExpandedRacecardRunnerRow";
import { Runner } from "../../../../../types/races/runner";
import { useAtom } from "jotai";
import { bookmakerPreferenceAtom, oddsFilterAtom } from "../../../../../atoms";
import { bestOddsForBookmaker } from "../../../../../client/odds";
import { OddsFilterOptionValue } from "../../../../Tabs/Sorter";
import { formatEquipmentText } from "../../../../../utils/race";
import Race from "../../../../../types/races/race";
import { OddBookmakers } from "../../../../../types/bookmaker";
import { HorseTrackerType } from "../../../../../types/races/horse";
import { Column } from "../../../../Racecard/Column";
import { BookmakerDisplayPreferred } from "../../../../../types/user";

type Props = {
  race: Race;
  runner: Runner;
  oddsFilterOptionValue?: OddsFilterOptionValue;
  nonRunner?: boolean;
  displaysPremiumData: boolean;
  bookmakers?: OddBookmakers;
  refreshListOfTrackedHorses: Function;
  trackedHorse?: HorseTrackerType;
  index?: number;
  firstRunnerWithRiQPromptId?: number;
};

/**
 * RacecardRunnerRow component
 * Displays a single runner row in a racecard.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Race} props.race - The race object
 * @param {Runner} props.runner - The runner object
 * @param {OddsFilterOptionValue} [props.oddsFilterOptionValue] - Odds filter option
 * @param {boolean} [props.nonRunner] - If the runner is a non-runner
 * @param {boolean} props.displaysPremiumData - Show premium data
 * @param {OddBookmakers} [props.bookmakers] - Bookmakers data
 * @param {Function} props.refreshListOfTrackedHorses - Refresh tracked horses
 * @param {HorseTrackerType} [props.trackedHorse] - Tracked horse info
 * @param {number} [props.index] - Index in the list
 * @param {number} [props.firstRunnerWithRiQPromptId] - The id of the first runner with a RaceiQ prompt, if applicable
 */

const RacecardRunnerRow = ({
  race,
  runner,
  nonRunner,
  displaysPremiumData,
  bookmakers,
  refreshListOfTrackedHorses,
  trackedHorse,
  index,
  firstRunnerWithRiQPromptId,
}: Props) => {
  const { isSmallAndDown, isMediumAndDown } = useResponsive();
  const [bookmakerPreference] = useAtom(bookmakerPreferenceAtom);

  const userSelectedBookmaker = (
    bookmakerPreference as BookmakerDisplayPreferred
  )?.bookmaker_id;

  const [oddsFilter] = useAtom(oddsFilterAtom);

  let oddsToUse;
  if (userSelectedBookmaker && oddsFilter.value === "preferred-bookmaker") {
    oddsToUse = bestOddsForBookmaker(runner.odds, userSelectedBookmaker);
  } else {
    oddsToUse = runner.bestOdds;
  }

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: !!trackedHorse
            ? colors.palette.rtv.tracker
            : "white",
        },
        index === undefined || index > 0 ? styles.topBorder : styles.noBorder,
        nonRunner ? styles.nonRunner : undefined,
      ]}
    >
      <View style={{ flexDirection: "row" }}>
        <Column
          name="position"
          style={{ flexDirection: "row", justifyContent: "flex-start" }}
        >
          <RunnerSilkAndNumbers
            status={runner.status}
            form={runner.form}
            silk={runner.silk}
            clothNumber={runner.cloth_number}
            barrierNumber={runner.barrier_number}
            textBadges={!!runner.text_badges.length}
          />
          <Gap size="xsmall" />
          <RunnerDetails
            runner={runner}
            displaysPremiumData={displaysPremiumData}
            trainerBadges={runner?.trainer_icon_badges}
            jockeyBadges={runner?.jockey_icon_badges}
          />
          {isMediumAndDown && <Gap size="small" />}
        </Column>
        {isSmallAndDown ? (
          <>
            <Column
              name="ageAndWeight"
              style={{
                justifyContent: "flex-start",
                minHeight: 25,
              }}
            >
              <View
                style={{ flexDirection: "row", justifyContent: "flex-end" }}
              >
                <RText>{runner.age}</RText>
                <Gap size="xxxsmall" />
                <RText style={{ minWidth: 38, textAlign: "center" }}>
                  {runner.format_weight}
                </RText>
                {formatEquipmentText(runner.equipment_text)}
                <Gap size="xxsmall" />
                <View style={{ justifyContent: "flex-start" }}>
                  <TimeformStarRating
                    displaysPremiumData={displaysPremiumData}
                    starRating={runner.timeform_star_rating}
                    formattedRating={runner.format_timeform_rating}
                  />
                </View>
              </View>

              <View
                style={{
                  alignItems: "center",
                  justifyContent: "flex-end",
                  flexDirection: "row",
                  paddingTop: 4,
                }}
              >
                {oddsToUse ? (
                  <StandardPriceButton
                    odd={oddsToUse}
                    runner={runner}
                    race={race}
                    bookmaker={bookmakers?.[oddsToUse.bookmaker_id] || null}
                    style={{ alignSelf: "flex-end" }}
                  />
                ) : (
                  <View style={{ width: 80, backgroundColor: "transparent" }} />
                )}
              </View>
            </Column>
            <Gap size="xxsmall" />
          </>
        ) : (
          <>
            <Column name="age" style={styles.center}>
              <RText>{runner.horse?.age || runner.age}</RText>
            </Column>
            <Column name="weight" style={styles.center}>
              <RText>{runner.format_weight}</RText>
              {formatEquipmentText(runner.equipment_text)}
            </Column>
            <Column name="originalRating" style={styles.center}>
              <RText>
                OR{" "}
                {runner.timeform_original_rating == 0
                  ? "-"
                  : runner.timeform_original_rating}
              </RText>
            </Column>
            <Column name="rtg" style={styles.center}>
              <TimeformStarRating
                displaysPremiumData={displaysPremiumData}
                starRating={runner.timeform_star_rating}
                formattedRating={runner.format_timeform_rating}
              />
            </Column>
            <Column name="odds" style={{ alignItems: "flex-end" }}>
              {oddsToUse ? (
                <StandardPriceButton
                  odd={oddsToUse}
                  race={race}
                  runner={runner}
                  bookmaker={bookmakers?.[oddsToUse.bookmaker_id] || null}
                />
              ) : (
                <View style={{ width: 95, backgroundColor: "transparent" }} />
              )}
            </Column>
          </>
        )}
      </View>
      <ExpandedRacecardRunnerRow
        runner={runner}
        displaysPremiumData={displaysPremiumData}
        bookmakers={bookmakers}
        refreshListOfTrackedHorses={refreshListOfTrackedHorses}
        trackedHorse={trackedHorse}
        index={index}
        firstRunnerWithRiQPromptId={firstRunnerWithRiQPromptId}
      />
    </View>
  );
};

export default RacecardRunnerRow;

const { colors } = variables;

const styles = StyleSheet.create({
  container: {
    paddingTop: 16,
    minWidth: "100%",
    maxWidth: "100%",
    display: "flex",
  },
  noBorder: {
    borderTopWidth: 0,
  },
  topBorder: {
    borderTopColor: colors.palette.neutral.x100,
    borderTopWidth: 1,
  },
  center: {
    alignItems: "center",
  },
  nonRunner: {
    opacity: 0.5,
  },
});
